import React, { useEffect, useState, useMemo } from "react";
import {
    Modal, Box, Typography, Button, TextField, Checkbox, FormControlLabel,
    FormControl, Select, MenuItem, InputLabel, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress, IconButton, Alert,
    RadioGroup, Radio, Divider, Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import PaymentsIcon from "@mui/icons-material/Payments";
import { PictureAsPdf, EditOutlined } from "@mui/icons-material";
import { TbImageInPicture } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import ProjectService from "../../services/ProjectService";
import UserService from "../../services/UserService";
import { useToast } from "../../contexts/ToastContext";
import styles from "./../css/InvoiceModals.module.css";

const underlineStyle = {
    '& .MuiInput-underline:before': { borderBottomColor: '#dce3ea' },
    '& .MuiInput-underline:hover:before': { borderBottomColor: '#427898' },
    '& .MuiInput-underline:after': { borderBottomColor: '#427898' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#427898' },
};

const MODES = [
    {
        key: 'ITEMIZED',
        icon: <AccessTimeIcon sx={{ color: '#427898', fontSize: 22 }} />,
        iconBg: '#eef4f8',
        labelKey: 'invoiceModal.modes.itemized',
        descKey: 'invoiceModal.modes.itemizedDesc',
    },
    {
        key: 'FIXED',
        icon: <AttachMoneyIcon sx={{ color: '#427898', fontSize: 22 }} />,
        iconBg: '#eef4f8',
        labelKey: 'invoiceModal.modes.fixed',
        descKey: 'invoiceModal.modes.fixedDesc',
    },
    {
        key: 'AKONTO',
        icon: <PaymentsIcon sx={{ color: '#0ea5e9', fontSize: 22 }} />,
        iconBg: '#e0f2fe',
        labelKey: 'invoiceModal.modes.akonto',
        descKey: 'invoiceModal.modes.akontoDesc',
    },
    {
        key: 'ALL_REMAINING',
        icon: <AllInclusiveIcon sx={{ color: '#d97706', fontSize: 22 }} />,
        iconBg: '#fef3c7',
        labelKey: 'invoiceModal.modes.allRemaining',
        descKey: 'invoiceModal.modes.allRemainingDesc',
    },
];

export default function GenerateInvoiceModal({ open, onClose, onSuccess, project, projectEntries, projectExpenses, projectServices, projectMaterials }) {
    const { t } = useTranslation();
    const { showToast } = useToast();

    // Mode selection
    const [selectedMode, setSelectedMode] = useState(null);
    const [isInvoiceOpen, setInvoiceOpen] = useState(false);

    // Item selections (ITEMIZED / FIXED optional / ALL_REMAINING auto)
    const [selectedEntries, setSelectedEntries] = useState([]);
    const [selectedExpenses, setSelectedExpenses] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [selectedMaterials, setSelectedMaterials] = useState([]);

    // FIXED / AKONTO
    const [fixedAmount, setFixedAmount] = useState('');
    const [fixedDescription, setFixedDescription] = useState('');

    // Rate fallback when project has no pricePerHour
    const [fallbackRate, setFallbackRate] = useState('');

    // Shared invoice fields
    const [tax, setTax] = useState(8.1);
    const [paymentTermsDays, setPaymentTermsDays] = useState(30);
    const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
    const [invoiceText, setInvoiceText] = useState(
        `${t('invoiceModal.sampleText.greeting')}\n${t('invoiceModal.sampleText.paymentTerms')}\n${t('invoiceModal.sampleText.closing')}`
    );
    const [showRabatt, setShowRabatt] = useState(false);
    const [rabattType, setRabattType] = useState('PERCENTAGE');
    const [rabattValue, setRabattValue] = useState('');
    const [includeQrCode, setIncludeQrCode] = useState(false);
    const [imageToInvoice, setImageToInvoice] = useState(null);

    // UI state
    const [hasBankingData, setHasBankingData] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Load banking data to know if QR is available
    useEffect(() => {
        if (!open) return;
        UserService.getCompanyBankInfo()
            .then(data => {
                const has = Array.isArray(data) && data.length > 0;
                setHasBankingData(has);
                setIncludeQrCode(has);
            })
            .catch(() => setHasBankingData(false));
    }, [open]);

    // When ALL_REMAINING is selected, auto-populate all uninvoiced items
    useEffect(() => {
        if (selectedMode !== 'ALL_REMAINING') return;
        const uninvEntries   = (projectEntries   || []).filter(e => !e.invoiced && !e.deleted);
        const uninvExpenses  = (projectExpenses  || []).filter(e => !e.invoiced && !e.deleted);
        const uninvServices  = (projectServices  || []).filter(s => !s.invoiced && !s.deleted);
        const uninvMaterials = (projectMaterials || []).filter(m => !m.invoiced && !m.deleted);
        setSelectedEntries(uninvEntries.map(e => e.id));
        setSelectedExpenses(uninvExpenses.map(e => e.id));
        setSelectedServices(uninvServices.map(s => s.id));
        setSelectedMaterials(uninvMaterials.map(m => m.id));
    }, [selectedMode]);

    const effectiveRate = useMemo(() => {
        const r = parseFloat(project?.pricePerHour || fallbackRate) || 0;
        return r;
    }, [project?.pricePerHour, fallbackRate]);

    const itemizedBaseAmount = useMemo(() => {
        const entriesTotal = (projectEntries || [])
            .filter(e => selectedEntries.includes(e.id))
            .reduce((s, e) => s + (e.workingHours * effectiveRate), 0);
        const expensesTotal = (projectExpenses || [])
            .filter(e => selectedExpenses.includes(e.id))
            .reduce((s, e) => s + (parseFloat(e.price) || 0), 0);
        const servicesTotal = (projectServices || [])
            .filter(s => selectedServices.includes(s.id))
            .reduce((s, sv) => s + ((sv.price || 0) * (sv.quantity || 0)), 0);
        const matsTotal = (projectMaterials || [])
            .filter(m => selectedMaterials.includes(m.id))
            .reduce((s, m) => s + (m.totalPrice || 0), 0);
        return entriesTotal + expensesTotal + servicesTotal + matsTotal;
    }, [selectedEntries, selectedExpenses, selectedServices, selectedMaterials, effectiveRate, projectEntries, projectExpenses, projectServices, projectMaterials]);

    const baseAmount = useMemo(() => {
        if (selectedMode === 'FIXED' || selectedMode === 'AKONTO') return parseFloat(fixedAmount) || 0;
        return itemizedBaseAmount;
    }, [selectedMode, fixedAmount, itemizedBaseAmount]);

    const rabattAmt = useMemo(() => {
        if (!showRabatt || !rabattValue) return 0;
        const rv = parseFloat(rabattValue) || 0;
        return rabattType === 'PERCENTAGE' ? (baseAmount * rv / 100) : rv;
    }, [showRabatt, rabattType, rabattValue, baseAmount]);

    const totalBeforeTax = Math.max(0, baseAmount - rabattAmt);
    const taxAmt = (totalBeforeTax * tax) / 100;
    const totalAmt = totalBeforeTax + taxAmt;

    // Overage alert
    const referenceValue = project?.contractedValue ?? project?.budget ?? null;
    const existingInvoicedTotal = project?.invoicedAmount || 0;
    const isOverage = referenceValue !== null && (existingInvoicedTotal + totalAmt) > referenceValue;

    const needsRateInput = (selectedMode === 'ITEMIZED' || selectedMode === 'ALL_REMAINING') && !project?.pricePerHour;

    const hasItemsSelected = selectedEntries.length > 0 || selectedExpenses.length > 0 ||
        selectedServices.length > 0 || selectedMaterials.length > 0;

    const canSave = useMemo(() => {
        if (isSaving) return false;
        if (selectedMode === 'FIXED' || selectedMode === 'AKONTO') return !!fixedAmount && parseFloat(fixedAmount) > 0;
        if (needsRateInput && !fallbackRate) return false;
        return hasItemsSelected;
    }, [isSaving, selectedMode, fixedAmount, needsRateInput, fallbackRate, hasItemsSelected]);

    const handleModeSelect = (mode) => {
        setSelectedMode(mode);
        setSelectedEntries([]);
        setSelectedExpenses([]);
        setSelectedServices([]);
        setSelectedMaterials([]);
        setFixedAmount('');
        setFixedDescription('');
        setInvoiceOpen(true);
    };

    const handleClose = () => {
        setSelectedMode(null);
        setInvoiceOpen(false);
        onClose();
    };

    const buildFormData = () => {
        const requestJson = {
            projectId: project.id,
            mode: selectedMode,
            ...(selectedMode === 'FIXED' || selectedMode === 'AKONTO'
                ? { fixedAmount: parseFloat(fixedAmount), fixedDescription }
                : {
                    selectedEntries,
                    selectedExpenses,
                    selectedServices,
                    selectedInventoryUsages: selectedMaterials,
                }
            ),
            ...(needsRateInput && fallbackRate ? { fallbackPricePerHour: parseFloat(fallbackRate) } : {}),
            rabattType: showRabatt && rabattValue ? rabattType : null,
            rabattValue: showRabatt && rabattValue ? parseFloat(rabattValue) : null,
            taxPercent: tax,
            paymentTermsDays,
            purchaseOrderNumber: purchaseOrderNumber || null,
            invoiceText,
            includeQrCode,
        };
        const fd = new FormData();
        fd.append('request', new Blob([JSON.stringify(requestJson)], { type: 'application/json' }));
        if (imageToInvoice) fd.append('imageToInvoice', imageToInvoice, imageToInvoice.name);
        return fd;
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await ProjectService.createInvoice(buildFormData());
            showToast(t('invoiceModal.toasts.invoiceCreated'), 'success');
            handleClose();
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error(err);
            showToast(t('invoiceModal.toasts.invoiceError'), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const fmt = (n) => n?.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00';

    const uninvoicedEntries   = (projectEntries   || []).filter(e => !e.invoiced && !e.deleted);
    const uninvoicedExpenses  = (projectExpenses  || []).filter(e => !e.invoiced && !e.deleted);
    const uninvoicedServices  = (projectServices  || []).filter(s => !s.invoiced && !s.deleted);
    const uninvoicedMaterials = (projectMaterials || []).filter(m => !m.invoiced && !m.deleted);

    return (
        <>
            {/* ── Mode Selection ────────────────────────────────────────────────── */}
            <Modal open={open && !isInvoiceOpen} onClose={handleClose}>
                <Box className={styles.selectSheet}>
                    <Box sx={{ height: 4, backgroundColor: '#427898', flexShrink: 0 }} />
                    <Box className={styles.selectHeader}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton size="small" onClick={handleClose} sx={{ color: '#64748b' }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#1a2332' }}>
                                {t('invoiceModal.selectType.title')}
                            </Typography>
                        </Box>
                        {referenceValue != null && (
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                {t('invoiceModal.referenceValue')}: <strong>CHF {fmt(referenceValue)}</strong>
                            </Typography>
                        )}
                    </Box>

                    <Box className={styles.selectBody}>
                        {MODES.map(({ key, icon, iconBg, labelKey, descKey }) => (
                            <Box
                                key={key}
                                onClick={() => handleModeSelect(key)}
                                sx={{
                                    cursor: 'pointer',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '12px',
                                    p: 2.5,
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    transition: 'all 0.15s',
                                    '&:hover': { borderColor: '#427898', backgroundColor: '#f8fafc' },
                                }}
                            >
                                <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {icon}
                                </Box>
                                <Box>
                                    <Typography fontWeight={700} sx={{ color: '#1a2332', mb: 0.25, lineHeight: 1.3 }}>
                                        {t(labelKey, key)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.5 }}>
                                        {t(descKey, '')}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Modal>

            {/* ── Invoice Detail Modal ──────────────────────────────────────────── */}
            <Modal open={isInvoiceOpen} onClose={handleClose}>
                <Box className={styles.fullscreenShell}>
                    <Box sx={{ height: 4, backgroundColor: '#427898', flexShrink: 0 }} />

                    {/* Header */}
                    <Box className={styles.stickyHeader}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton size="small" onClick={handleClose} sx={{ color: '#64748b' }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#1a2332' }}>
                                {t(`invoiceModal.modes.${selectedMode?.toLowerCase()}`, selectedMode)}
                            </Typography>
                            <Chip label={selectedMode} size="small" sx={{ backgroundColor: '#eef4f8', color: '#427898', fontWeight: 600 }} />
                        </Box>
                        <Button
                            variant="contained" size="small"
                            disabled={!canSave}
                            endIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
                            onClick={handleSave}
                            sx={{ backgroundColor: '#427898', borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                        >
                            {t('invoiceModal.actions.saveInvoice')}
                        </Button>
                    </Box>

                    <Box className={styles.contentRow}>
                        {/* ── LEFT PANE ── */}
                        <Box className={styles.leftPaneWide}>
                            <Box className={styles.leftPaneContent}>

                                {/* Overage alert */}
                                {isOverage && (
                                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
                                        {t('invoiceModal.overageAlert', {
                                            total: fmt(existingInvoicedTotal + totalAmt),
                                            reference: fmt(referenceValue),
                                        })}
                                    </Alert>
                                )}

                                {/* Rate input (when project has no pricePerHour) */}
                                {needsRateInput && (
                                    <Box sx={{ mb: 3 }}>
                                        <Alert severity="info" sx={{ mb: 1.5, borderRadius: '10px' }}>
                                            {t('invoiceModal.noRateSet')}
                                        </Alert>
                                        <TextField
                                            label={t('invoiceModal.fallbackRate')}
                                            type="number" size="small" variant="standard"
                                            sx={{ ...underlineStyle, width: 220 }}
                                            value={fallbackRate}
                                            onChange={e => setFallbackRate(e.target.value)}
                                            InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: '#64748b' }}>CHF</Typography> }}
                                        />
                                    </Box>
                                )}

                                {/* FIXED / AKONTO amount */}
                                {(selectedMode === 'FIXED' || selectedMode === 'AKONTO') && (
                                    <Box sx={{ mb: 3 }}>
                                        <Box sx={{ borderLeft: '3px solid #427898', pl: 1.5, py: 0.25, mb: 1.5 }}>
                                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a2332' }}>
                                                {t('invoiceModal.amount')}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                            <TextField
                                                label={t('invoiceModal.fixedAmount')}
                                                type="number" size="small" variant="standard"
                                                sx={{ ...underlineStyle, width: 200 }}
                                                value={fixedAmount}
                                                onChange={e => setFixedAmount(e.target.value)}
                                                InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: '#64748b' }}>CHF</Typography> }}
                                            />
                                            <TextField
                                                label={t('invoiceModal.fixedDescription')}
                                                size="small" variant="standard"
                                                sx={{ ...underlineStyle, flex: 1, minWidth: 200 }}
                                                value={fixedDescription}
                                                onChange={e => setFixedDescription(e.target.value)}
                                            />
                                        </Box>
                                    </Box>
                                )}

                                {/* Items selection (ITEMIZED and FIXED optional) */}
                                {(selectedMode === 'ITEMIZED' || selectedMode === 'FIXED' || selectedMode === 'ALL_REMAINING') && (
                                    <>
                                        {/* Work entries */}
                                        {uninvoicedEntries.length > 0 && (
                                            <ItemSection
                                                title={t('invoiceModal.sections.entries')}
                                                readonly={selectedMode === 'ALL_REMAINING'}
                                            >
                                                <TableContainer>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                {selectedMode !== 'ALL_REMAINING' && <TableCell padding="checkbox" />}
                                                                <TableCell>{t('labels.date')}</TableCell>
                                                                <TableCell>{t('labels.description')}</TableCell>
                                                                <TableCell align="right">{t('labels.hours')}</TableCell>
                                                                <TableCell align="right">CHF</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {uninvoicedEntries.map(e => (
                                                                <TableRow key={e.id} hover selected={selectedEntries.includes(e.id)}
                                                                    onClick={selectedMode !== 'ALL_REMAINING' ? () => setSelectedEntries(prev => prev.includes(e.id) ? prev.filter(x => x !== e.id) : [...prev, e.id]) : undefined}
                                                                    sx={{ cursor: selectedMode !== 'ALL_REMAINING' ? 'pointer' : 'default' }}
                                                                >
                                                                    {selectedMode !== 'ALL_REMAINING' && (
                                                                        <TableCell padding="checkbox">
                                                                            <Checkbox size="small" checked={selectedEntries.includes(e.id)} />
                                                                        </TableCell>
                                                                    )}
                                                                    <TableCell sx={{ fontSize: 12 }}>{e.entryDate}</TableCell>
                                                                    <TableCell sx={{ fontSize: 12 }}>{e.description}</TableCell>
                                                                    <TableCell align="right" sx={{ fontSize: 12 }}>{e.workingHours}h</TableCell>
                                                                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>
                                                                        {fmt(e.workingHours * effectiveRate)}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </ItemSection>
                                        )}

                                        {/* Expenses */}
                                        {uninvoicedExpenses.length > 0 && (
                                            <ItemSection title={t('invoiceModal.sections.expenses')} readonly={selectedMode === 'ALL_REMAINING'}>
                                                <TableContainer>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                {selectedMode !== 'ALL_REMAINING' && <TableCell padding="checkbox" />}
                                                                <TableCell>{t('labels.description')}</TableCell>
                                                                <TableCell align="right">CHF</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {uninvoicedExpenses.map(e => (
                                                                <TableRow key={e.id} hover selected={selectedExpenses.includes(e.id)}
                                                                    onClick={selectedMode !== 'ALL_REMAINING' ? () => setSelectedExpenses(prev => prev.includes(e.id) ? prev.filter(x => x !== e.id) : [...prev, e.id]) : undefined}
                                                                    sx={{ cursor: selectedMode !== 'ALL_REMAINING' ? 'pointer' : 'default' }}
                                                                >
                                                                    {selectedMode !== 'ALL_REMAINING' && (
                                                                        <TableCell padding="checkbox">
                                                                            <Checkbox size="small" checked={selectedExpenses.includes(e.id)} />
                                                                        </TableCell>
                                                                    )}
                                                                    <TableCell sx={{ fontSize: 12 }}>{e.description}</TableCell>
                                                                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{fmt(e.price)}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </ItemSection>
                                        )}

                                        {/* Services */}
                                        {uninvoicedServices.length > 0 && (
                                            <ItemSection title={t('invoiceModal.sections.services')} readonly={selectedMode === 'ALL_REMAINING'}>
                                                <TableContainer>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                {selectedMode !== 'ALL_REMAINING' && <TableCell padding="checkbox" />}
                                                                <TableCell>{t('labels.name')}</TableCell>
                                                                <TableCell align="right">{t('labels.qty')}</TableCell>
                                                                <TableCell align="right">CHF</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {uninvoicedServices.map(s => (
                                                                <TableRow key={s.id} hover selected={selectedServices.includes(s.id)}
                                                                    onClick={selectedMode !== 'ALL_REMAINING' ? () => setSelectedServices(prev => prev.includes(s.id) ? prev.filter(x => x !== s.id) : [...prev, s.id]) : undefined}
                                                                    sx={{ cursor: selectedMode !== 'ALL_REMAINING' ? 'pointer' : 'default' }}
                                                                >
                                                                    {selectedMode !== 'ALL_REMAINING' && (
                                                                        <TableCell padding="checkbox">
                                                                            <Checkbox size="small" checked={selectedServices.includes(s.id)} />
                                                                        </TableCell>
                                                                    )}
                                                                    <TableCell sx={{ fontSize: 12 }}>{s.name || s.subServiceName}</TableCell>
                                                                    <TableCell align="right" sx={{ fontSize: 12 }}>{s.quantity}</TableCell>
                                                                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{fmt((s.price || 0) * (s.quantity || 0))}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </ItemSection>
                                        )}

                                        {/* Materials */}
                                        {uninvoicedMaterials.length > 0 && (
                                            <ItemSection title={t('invoiceModal.sections.materials')} readonly={selectedMode === 'ALL_REMAINING'}>
                                                <TableContainer>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                {selectedMode !== 'ALL_REMAINING' && <TableCell padding="checkbox" />}
                                                                <TableCell>{t('labels.name')}</TableCell>
                                                                <TableCell align="right">{t('labels.qty')}</TableCell>
                                                                <TableCell align="right">CHF</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {uninvoicedMaterials.map(m => (
                                                                <TableRow key={m.id} hover selected={selectedMaterials.includes(m.id)}
                                                                    onClick={selectedMode !== 'ALL_REMAINING' ? () => setSelectedMaterials(prev => prev.includes(m.id) ? prev.filter(x => x !== m.id) : [...prev, m.id]) : undefined}
                                                                    sx={{ cursor: selectedMode !== 'ALL_REMAINING' ? 'pointer' : 'default' }}
                                                                >
                                                                    {selectedMode !== 'ALL_REMAINING' && (
                                                                        <TableCell padding="checkbox">
                                                                            <Checkbox size="small" checked={selectedMaterials.includes(m.id)} />
                                                                        </TableCell>
                                                                    )}
                                                                    <TableCell sx={{ fontSize: 12 }}>{m.inventoryName || m.name}</TableCell>
                                                                    <TableCell align="right" sx={{ fontSize: 12 }}>{m.quantity}</TableCell>
                                                                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>{fmt(m.totalPrice)}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </ItemSection>
                                        )}
                                    </>
                                )}

                                {/* Invoice text */}
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ borderLeft: '3px solid #427898', pl: 1.5, py: 0.25, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a2332' }}>
                                            {t('invoiceModal.details.text')}
                                        </Typography>
                                        <EditOutlined sx={{ fontSize: 14, color: '#94a3b8' }} />
                                    </Box>
                                    <TextField
                                        multiline rows={3} fullWidth variant="standard"
                                        sx={underlineStyle}
                                        value={invoiceText}
                                        onChange={e => setInvoiceText(e.target.value)}
                                    />
                                    <Box sx={{ mt: 1.5 }}>
                                        <Button
                                            variant="outlined" component="label" size="small"
                                            startIcon={<TbImageInPicture />}
                                            sx={{ borderColor: '#427898', color: '#427898', borderRadius: '8px', textTransform: 'none' }}
                                        >
                                            {imageToInvoice ? imageToInvoice.name : t('invoiceModal.attachPicture')}
                                            <input type="file" accept="image/*" hidden onChange={e => {
                                                const f = e.target.files[0];
                                                if (f && f.type.startsWith('image/')) setImageToInvoice(f);
                                            }} />
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {/* ── RIGHT SIDEBAR ── */}
                        <Box className={styles.rightPane}>

                            {/* Tax */}
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    {t('invoiceModal.tax')}
                                </Typography>
                                <FormControl fullWidth size="small" variant="standard" sx={{ mt: 0.5, ...underlineStyle }}>
                                    <Select value={tax} onChange={e => setTax(e.target.value)}>
                                        <MenuItem value={0}>0%</MenuItem>
                                        <MenuItem value={2.6}>2.6%</MenuItem>
                                        <MenuItem value={3.8}>3.8%</MenuItem>
                                        <MenuItem value={8.1}>8.1%</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Payment terms */}
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    {t('invoiceModal.paymentTerms')}
                                </Typography>
                                <FormControl fullWidth size="small" variant="standard" sx={{ mt: 0.5, ...underlineStyle }}>
                                    <Select value={paymentTermsDays} onChange={e => setPaymentTermsDays(e.target.value)}>
                                        <MenuItem value={0}>{t('invoiceModal.dueOnReceipt')}</MenuItem>
                                        <MenuItem value={15}>15 {t('common.days')}</MenuItem>
                                        <MenuItem value={30}>30 {t('common.days')}</MenuItem>
                                        <MenuItem value={45}>45 {t('common.days')}</MenuItem>
                                        <MenuItem value={60}>60 {t('common.days')}</MenuItem>
                                        <MenuItem value={90}>90 {t('common.days')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Purchase order number */}
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    label={t('invoiceModal.purchaseOrderNumber')}
                                    size="small" fullWidth variant="standard"
                                    sx={underlineStyle}
                                    value={purchaseOrderNumber}
                                    onChange={e => setPurchaseOrderNumber(e.target.value)}
                                />
                            </Box>

                            {/* QR code */}
                            {hasBankingData && (
                                <Box sx={{ mb: 2 }}>
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={includeQrCode} onChange={e => setIncludeQrCode(e.target.checked)} />}
                                        label={<Typography variant="body2">{t('invoiceModal.includeQrCode')}</Typography>}
                                    />
                                </Box>
                            )}

                            {/* Discount */}
                            <Box sx={{ mb: 2 }}>
                                <FormControlLabel
                                    control={<Checkbox size="small" checked={showRabatt} onChange={e => setShowRabatt(e.target.checked)} />}
                                    label={<Typography variant="body2">{t('invoiceModal.addDiscount')}</Typography>}
                                />
                                {showRabatt && (
                                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                        <FormControl size="small" variant="standard" sx={{ width: 120, ...underlineStyle }}>
                                            <Select value={rabattType} onChange={e => setRabattType(e.target.value)}>
                                                <MenuItem value="PERCENTAGE">%</MenuItem>
                                                <MenuItem value="AMOUNT">CHF</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            type="number" size="small" variant="standard"
                                            sx={{ ...underlineStyle, flex: 1 }}
                                            value={rabattValue}
                                            onChange={e => setRabattValue(e.target.value)}
                                            placeholder="0"
                                        />
                                    </Box>
                                )}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Financial summary */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <SummaryRow label={t('invoiceModal.summary.subtotal')} value={`CHF ${fmt(baseAmount)}`} />
                                {rabattAmt > 0 && <SummaryRow label={t('invoiceModal.summary.discount')} value={`- CHF ${fmt(rabattAmt)}`} color="#ef4444" />}
                                {rabattAmt > 0 && <SummaryRow label={t('invoiceModal.summary.afterDiscount')} value={`CHF ${fmt(totalBeforeTax)}`} />}
                                <SummaryRow label={`${t('invoiceModal.summary.tax')} (${tax}%)`} value={`CHF ${fmt(taxAmt)}`} />
                                <Divider />
                                <SummaryRow label={t('invoiceModal.summary.total')} value={`CHF ${fmt(totalAmt)}`} bold />
                            </Box>

                            {referenceValue != null && (
                                <Box sx={{ mt: 2, p: 1.5, borderRadius: '8px', backgroundColor: isOverage ? '#fef2f2' : '#f8fafc', border: `1px solid ${isOverage ? '#fca5a5' : '#e2e8f0'}` }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                        {t('invoiceModal.referenceValue')}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: isOverage ? '#dc2626' : '#1a2332' }}>
                                        CHF {fmt(referenceValue)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </>
    );
}

function ItemSection({ title, readonly, children }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ borderLeft: '3px solid #427898', pl: 1.5, py: 0.25, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a2332' }}>{title}</Typography>
                {readonly && <Chip label="auto" size="small" sx={{ height: 18, fontSize: 10, backgroundColor: '#fef3c7', color: '#92400e' }} />}
            </Box>
            {children}
        </Box>
    );
}

function SummaryRow({ label, value, bold, color }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>{label}</Typography>
            <Typography variant="body2" fontWeight={bold ? 700 : 400} sx={{ color: color || (bold ? '#1a2332' : '#475569') }}>
                {value}
            </Typography>
        </Box>
    );
}
